/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 call console: everything about the caller beside the call.
 *
 * Who they are, the numbers that matter (open deals, cases and tasks), their
 * open tasks, and one timeline of past calls, WhatsApp conversations and
 * activities, plus their deals and cases. Data comes from endpoints that
 * already enforce permissions; a section the agent may not see (403) or that
 * has nothing (404) is simply empty, never an error.
 */

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  Briefcase,
  Building2,
  CalendarClock,
  CheckSquare,
  History,
  LifeBuoy,
  Mail,
  MessageCircle,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  User,
  UserSearch,
} from "lucide-react";
import { useAuth } from "auth/Auth";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import { relativeTime } from "layouts/crm/account360/format";

const TYPE_META = {
  contact: { icon: User, label: "Contact", path: "contacts" },
  account: { icon: Building2, label: "Account", path: "accounts" },
  lead: { icon: UserSearch, label: "Lead", path: "leads" },
};
const PARENT = { contact: "contacts", account: "accounts", lead: "leads" };
const DONE = new Set(["completed", "cancelled", "canceled", "closed"]);
const openStatus = (s) => !DONE.has(String(s || "").toLowerCase());

const getList = (axiosInstance, url, params) =>
  axiosInstance
    .get(url, { params })
    .then((r) => (Array.isArray(r.data) ? r.data : []))
    .catch(() => []); // 403 / 404 / network: the section is just empty

function Section({ icon: Icon, title, count, children }) {
  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.25, py: 1.5, borderBottom: `1px solid ${t.border}` }}>
        <Icon size={16} color={t.text2} aria-hidden="true" />
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: t.text, flex: 1 }}>
          {title}
        </Typography>
        {count != null && <Typography sx={{ fontSize: 12.5, color: t.text3 }}>{count}</Typography>}
      </Box>
      <Box sx={{ px: 2.25, py: 1.5 }}>{children}</Box>
    </Card>
  );
}
Section.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, count: PropTypes.number, children: PropTypes.node };
Section.defaultProps = { count: null, children: null };

function Empty({ children }) {
  return <Typography sx={{ fontSize: 13.5, color: t.text3, py: 0.5 }}>{children}</Typography>;
}
Empty.propTypes = { children: PropTypes.node };
Empty.defaultProps = { children: null };

function Kpi({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: t.text3, fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: t.text, lineHeight: 1.3 }}>{value}</Typography>
    </Box>
  );
}
Kpi.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };
Kpi.defaultProps = { value: "—" };

function Row({ title, meta, href }) {
  const body = (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: href ? t.link : t.text, whiteSpace: "normal", wordBreak: "break-word" }}>{title}</Typography>
      {meta && <Typography sx={{ fontSize: 12.5, color: t.text2 }}>{meta}</Typography>}
    </Box>
  );
  return href ? (
    <ButtonBase
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: "flex", width: "100%", textAlign: "left", py: 0.75, borderRadius: "6px", fontFamily: "inherit", "&:hover": { bgcolor: t.hover } }}
    >
      {body}
    </ButtonBase>
  ) : (
    <Box sx={{ display: "flex", py: 0.75 }}>{body}</Box>
  );
}
Row.propTypes = { title: PropTypes.node.isRequired, meta: PropTypes.node, href: PropTypes.string };
Row.defaultProps = { meta: null, href: null };

/** Calls/WhatsApp conversations and activities as one newest-first list. */
export function customerTimeline(activities = [], conversations = [], limit = 12) {
  const items = [];
  activities.forEach((a) => {
    items.push({
      id: `a-${a.id}`,
      icon: String(a.type || "").toLowerCase() === "call" ? Phone : CheckSquare,
      tone: String(a.type || "").toLowerCase() === "call" ? v2Tones.success : v2Tones.info,
      title: a.title || a.type || "Activity",
      meta: [a.type, a.status, a.assigned_to_name].filter(Boolean).join(" · "),
      at: a.updated_at || a.created_at || a.due_date,
    });
  });
  conversations.forEach((c) => {
    const whatsapp = String(c.channel || "").toLowerCase().includes("whatsapp");
    const outbound = c.direction === "outbound";
    items.push({
      id: `c-${c.id}`,
      icon: whatsapp ? MessageCircle : outbound ? PhoneOutgoing : PhoneIncoming,
      tone: whatsapp ? v2Tones.success : v2Tones.neutral,
      title: `${whatsapp ? "WhatsApp" : outbound ? "Outbound call" : "Inbound call"}${c.disposition_name ? ` · ${c.disposition_name}` : ""}`,
      meta: [c.assigned_to_name, c.wrap_up_notes].filter(Boolean).join(" · "),
      at: c.started_at || c.created_at,
    });
  });
  return items
    .filter((i) => i.at)
    .sort((x, y) => new Date(y.at) - new Date(x.at))
    .slice(0, limit);
}

export default function CustomerPanel({ lookup, resolved, currentCallId }) {
  const { axiosInstance } = useAuth();
  const [activities, setActivities] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  const type = resolved?.type;
  const recordId = resolved?.id;
  const parentTable = PARENT[type];

  useEffect(() => {
    let alive = true;
    if (!parentTable || !recordId) {
      setActivities([]);
      setConversations([]);
      return undefined;
    }
    setLoading(true);
    Promise.all([
      getList(axiosInstance, "/activities/", { parent_table: parentTable, parent_table_id: recordId }),
      getList(axiosInstance, `/call-console/records/${type}/${recordId}/calls`, { limit: 25 }),
    ]).then(([acts, convs]) => {
      if (!alive) return;
      setActivities(acts);
      setConversations(convs.filter((c) => c.id !== currentCallId));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [axiosInstance, parentTable, recordId, type, currentCallId]);

  const related = lookup?.related || {};
  const deals = related.deals || [];
  const cases = related.cases || [];
  const openTasks = useMemo(
    () => activities.filter((a) => openStatus(a.status) && String(a.type || "").toLowerCase() !== "call")
      .sort((x, y) => new Date(x.due_date || 0) - new Date(y.due_date || 0)),
    [activities]
  );
  const timeline = useMemo(() => customerTimeline(activities, conversations), [activities, conversations]);
  const lastAt = timeline[0]?.at;

  if (!lookup) {
    return (
      <Card sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text }}>No caller yet</Typography>
        <Typography sx={{ fontSize: 13.5, color: t.text2, mt: 0.5 }}>
          Take or place a call, or look up a number, and the customer&apos;s history shows here.
        </Typography>
      </Card>
    );
  }

  const meta = TYPE_META[type] || { icon: User, label: "Caller", path: null };
  const TypeIcon = meta.icon;
  const phone = lookup.e164 || lookup.phone;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Who */}
      <Card sx={{ p: 2.25 }}>
        <Box display="flex" gap={1.5} alignItems="center">
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: v2Tones.info.bg, color: v2Tones.info.fg, flexShrink: 0 }}>
            <TypeIcon size={20} aria-hidden="true" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: t.text3 }}>
              {resolved ? meta.label : "Unknown caller"}
            </Typography>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: t.text, lineHeight: 1.25, wordBreak: "break-word" }}>
              {resolved?.name || phone || "Caller"}
            </Typography>
          </Box>
          {resolved && meta.path && (
            <ButtonBase
              component="a"
              href={`/${meta.path}/${resolved.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: t.link, px: 1, py: 0.5, borderRadius: "6px", "&:hover": { textDecoration: "underline" } }}
            >
              Open record
            </ButtonBase>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2} mt={1.5}>
          {phone && (
            <Box component="a" href={`tel:${phone}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 13.5, color: t.link, textDecoration: "none" }}>
              <Phone size={14} aria-hidden="true" /> {phone}
            </Box>
          )}
          {resolved?.email && (
            <Box component="a" href={`mailto:${resolved.email}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, fontSize: 13.5, color: t.link, textDecoration: "none" }}>
              <Mail size={14} aria-hidden="true" /> {resolved.email}
            </Box>
          )}
          {resolved?.subtitle && <Typography sx={{ fontSize: 13.5, color: t.text2 }}>{resolved.subtitle}</Typography>}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${t.border}` }}>
          <Kpi label="Open deals" value={deals.length} />
          <Kpi label="Open cases" value={cases.filter((c) => openStatus(c.status)).length} />
          <Kpi label="Open tasks" value={resolved ? openTasks.length : "—"} />
          <Kpi label="Last contact" value={lastAt ? relativeTime(lastAt) : "—"} />
        </Box>
      </Card>

      {!resolved ? (
        <Card sx={{ p: 2.25 }}>
          <Typography sx={{ fontSize: 13.5, color: t.text2 }}>
            {lookup.matches?.length > 1
              ? "This number belongs to more than one record. Choose who you are speaking to under “Who was this call about?”."
              : "Nothing in the CRM matches this number yet. You can create a lead from the wrap-up."}
          </Typography>
        </Card>
      ) : null}

      {resolved && (
        <Section icon={CalendarClock} title="Open tasks" count={openTasks.length}>
          {loading ? <CircularProgress size={18} /> : openTasks.length === 0 ? (
            <Empty>No open tasks.</Empty>
          ) : (
            openTasks.slice(0, 6).map((a) => (
              <Row
                key={a.id}
                title={a.title || a.type}
                meta={[a.type, a.due_date ? `due ${new Date(a.due_date).toLocaleString()}` : null, a.assigned_to_name].filter(Boolean).join(" · ")}
              />
            ))
          )}
        </Section>
      )}

      {resolved && (
        <Section icon={History} title="History" count={timeline.length}>
          {loading ? <CircularProgress size={18} /> : timeline.length === 0 ? (
            <Empty>No previous calls, messages or activities.</Empty>
          ) : (
            <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
              {timeline.map((e, i) => {
                const Icon = e.icon;
                const last = i === timeline.length - 1;
                return (
                  <Box component="li" key={e.id} sx={{ position: "relative", display: "flex", gap: 1.5, pb: last ? 0 : 1.75 }}>
                    {!last && <Box aria-hidden="true" sx={{ position: "absolute", left: 14, top: 30, bottom: 2, width: "1px", bgcolor: t.border }} />}
                    <Box sx={{ width: 29, height: 29, flexShrink: 0, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: e.tone.bg, color: e.tone.fg, boxShadow: `0 0 0 3px ${t.surface}` }}>
                      <Icon size={14} aria-hidden="true" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: t.text, flex: 1, minWidth: 0, whiteSpace: "normal", wordBreak: "break-word" }}>
                          {e.title}
                        </Typography>
                        <Typography component="time" dateTime={e.at} sx={{ fontSize: 12, color: t.text3, whiteSpace: "nowrap" }}>
                          {relativeTime(e.at)}
                        </Typography>
                      </Box>
                      {e.meta && <Typography sx={{ fontSize: 12.5, color: t.text2, mt: 0.25, wordBreak: "break-word" }}>{e.meta}</Typography>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Section>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Section icon={Briefcase} title="Deals" count={deals.length}>
          {deals.length === 0 ? <Empty>No deals.</Empty> : deals.slice(0, 6).map((d) => (
            <Row key={d.id} title={d.name || `Deal ${d.id}`} meta={[d.stage, d.value != null ? String(d.value) : null].filter(Boolean).join(" · ")} href={`/deals/${d.id}`} />
          ))}
        </Section>
        <Section icon={LifeBuoy} title="Cases" count={cases.length}>
          {cases.length === 0 ? <Empty>No cases.</Empty> : cases.slice(0, 6).map((c) => (
            <Row key={c.id} title={c.subject || c.case_number || `Case ${c.id}`} meta={[c.case_number, c.status].filter(Boolean).join(" · ")} href={`/cases/${c.id}`} />
          ))}
        </Section>
      </Box>
    </Box>
  );
}

CustomerPanel.propTypes = {
  lookup: PropTypes.object,
  resolved: PropTypes.object,
  currentCallId: PropTypes.number,
};
CustomerPanel.defaultProps = { lookup: null, resolved: null, currentCallId: null };
